import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select } from '@mui/material';
import { PRES } from './chrome';
import {
  firstOpenLanguageMarket,
  isLanguageMarketTaken,
} from './languageVersionHelpers';
import { COPY, languageLabel, marketLabel } from './presentationCopy';
import { PresentationFamilyMember, PresentationLanguage, PresentationMarket } from './types';
import { usePresentationSettings } from './PresentationSettingsContext';
import {
  settingsLanguageLabel,
  settingsMarketLabel,
  visiblePresentationLanguages,
  visiblePresentationMarkets,
} from './presentationSettings';

interface LanguageVersionDialogProps {
  open: boolean;
  members: PresentationFamilyMember[];
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onCreate: (input: { language: PresentationLanguage; market: PresentationMarket }) => Promise<void>;
}

const LanguageVersionDialog: React.FC<LanguageVersionDialogProps> = ({
  open, members, busy, error, onClose, onCreate,
}) => {
  const { settings } = usePresentationSettings();
  const visibleLanguages = visiblePresentationLanguages(settings);
  const visibleMarkets = visiblePresentationMarkets(settings);
  const openPair = firstOpenLanguageMarket(members, visibleLanguages, visibleMarkets);
  const [language, setLanguage] = useState<PresentationLanguage>(openPair?.language || 'fi');
  const [market, setMarket] = useState<PresentationMarket>(openPair?.market || 'FI');

  useEffect(() => {
    if (!open) return;
    const next = firstOpenLanguageMarket(members, visibleLanguages, visibleMarkets);
    if (next) {
      setLanguage(next.language);
      setMarket(next.market);
    }
  }, [open, members, visibleLanguages, visibleMarkets]);

  const taken = isLanguageMarketTaken(members, language, market);
  const noneOpen = !openPair;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{COPY.createLanguageVersion}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Alert severity="info">{COPY.languageVersionHelp}</Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <Select
          size="small"
          value={language}
          onChange={(event) => setLanguage(event.target.value as PresentationLanguage)}
        >
          {visibleLanguages.map((value) => (
            <MenuItem
              key={value}
              value={value}
              disabled={isLanguageMarketTaken(members, value, market) && !(language === value)}
            >
              {languageLabel(value, settingsLanguageLabel(settings, value))}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={market}
          onChange={(event) => setMarket(event.target.value as PresentationMarket)}
        >
          {visibleMarkets.map((value) => (
            <MenuItem
              key={value}
              value={value}
              disabled={isLanguageMarketTaken(members, language, value) && !(market === value)}
            >
              {marketLabel(value, settingsMarketLabel(settings, value))}
            </MenuItem>
          ))}
        </Select>
        {(taken || noneOpen) && <Alert severity="warning">{COPY.languageVersionPairTaken}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
        <Button
          disabled={busy || taken || noneOpen}
          variant="contained"
          sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
          onClick={() => { void onCreate({ language, market }); }}
        >
          {COPY.createLanguageVersion}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LanguageVersionDialog;
